import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare let fbq: any;

@Injectable({ providedIn: 'root' })
export class MetaPixelInitService {
  private isInitialized = false;

  // ✅ EVENT ID GENERATOR (DEDUP)
  generateEventId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }

  initPixel() {
    if (this.isInitialized) return;

    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;

      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };

      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];

      t = b.createElement(e);
      t.async = true;
      t.src = v;

      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js'
    );

    fbq('init', environment.metaPixelId);

    // 🔥 TEST MODE (REMOVE AFTER GO LIVE)
    if (environment.production === false) {
      fbq('set', 'test_event_code', 'TEST12345');
    }

    fbq('track', 'PageView');

    this.isInitialized = true;
  }

  // ✅ SAFE TRACK
  private safeTrack(eventName: string, data: any, eventId: string) {
    if (typeof fbq !== 'undefined') {
      fbq('track', eventName, data, { eventID: eventId });
    }
  }

  // ✅ GENERIC TRACK
  trackEvent(eventName: string, data: any, prefix: string) {
    const eventId = this.generateEventId(prefix);
    this.safeTrack(eventName, data, eventId);
    return eventId;
  }

  // =========================
  // 🎯 STANDARD EVENTS
  // =========================

  trackViewContent(product: any) {
    const eventId = product.eventId || this.generateEventId('viewcontent');
    this.safeTrack('ViewContent', {
      content_ids: [product.id],
      content_type: product.contentType || 'product',
      content_name: product.name,
      content_category: product.category,
      value: product.price,
      currency: product.currency || 'INR',
    }, eventId);
    return eventId;
  }

  trackAddToCart(payload: any) {
    // NOTE: eventId is generated here and returned so the component can pass it
    // to the backend CAPI call as x-event-id header (see product-details.component.ts)
    const eventId = this.generateEventId('addtocart');
    this.safeTrack('AddToCart', {
      content_ids: [payload.id],
      content_type: 'product',
      content_name: payload.name,
      value: payload.price,
      currency: payload.currency || 'INR',
      num_items: payload.quantity || 1,
    }, eventId);
    return eventId;
  }

  trackInitiateCheckout(payload: any) {
    const eventId = payload.eventId || this.generateEventId('checkout');
    this.safeTrack('InitiateCheckout', {
      content_ids: payload.contentIds,
      content_type: 'product',
      value: payload.value,
      currency: payload.currency || 'INR',
      num_items: payload.numItems,
    }, eventId);
    return eventId;
  }

  trackPurchase(payload: any) {
    const eventId = payload.eventId || this.generateEventId('purchase');
    this.safeTrack('Purchase', {
      content_ids: payload.contentIds,
      content_type: 'product',
      value: payload.value,
      currency: payload.currency || 'INR',
      num_items: payload.numItems,
    }, eventId);
    return eventId;
  }

  trackSearch(payload: any) {
    const eventId = payload.eventId || this.generateEventId('search');
    this.safeTrack('Search', {
      search_string: payload.searchString,
      content_ids: payload.contentIds || [],
    }, eventId);
    return eventId;
  }

  trackLead(payload: any) {
    const eventId = payload.eventId || this.generateEventId('lead');
    this.safeTrack('Lead', {
      content_name: payload.contentName || 'Contact Form',
      content_category: payload.contentCategory || 'lead',
    }, eventId);
    return eventId;
  }

  // =========================
  // 🔥 FIXED MISSING EVENTS
  // =========================

  trackAddToWishlist(payload: any) {
    const eventId = payload.eventId || this.generateEventId('wishlist');
    this.safeTrack('AddToWishlist', {
      content_ids: [payload.id],
      content_type: 'product',
      content_name: payload.name,
      value: payload.price,
      currency: payload.currency || 'INR',
    }, eventId);
    return eventId;
  }

  trackCompleteRegistration(payload: any) {
    const eventId = payload.eventId || this.generateEventId('register');
    this.safeTrack('CompleteRegistration', {
      content_name: payload?.name || 'User Registration',
    }, eventId);
    return eventId;
  }
}